import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Button, CircularProgress, Alert, Chip, Stack, Tabs, Tab, Card, CardContent,
} from '@mui/material';
import {
  FactCheck as CheckIcon, Refresh as RefreshIcon, Info as InfoIcon,
} from '@mui/icons-material';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';

const SEV = { high: 'error', medium: 'warning', low: 'default', info: 'info' };
const SEV_T = { high: 'высокая', medium: 'средняя', low: 'низкая', info: 'инфо' };

function SystemChecks() {
  const { user } = useAuth();
  const can = (p) => user?.is_superuser || user?.permissions?.includes(p);

  const [tab, setTab] = useState(0);   // 0 = open, 1 = resolved
  const [rows, setRows] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [log, dg] = await Promise.all([
        axios.get('/notifications/log', { params: { resolved: tab === 1 } }),
        axios.get('/notifications/digest'),
      ]);
      setRows(log.data || []);
      setDigest(dg.data && dg.data.title ? dg.data : null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const scanNow = async () => {
    setScanning(true); setError('');
    try {
      await axios.post('/notifications/scan');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setScanning(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckIcon color="primary" />
          <Typography variant="h4">Системные проверки</Typography>
        </Box>
        {can('issues.view') && (
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={scanNow} disabled={scanning}>
            {scanning ? 'Сканирую…' : 'Сканировать сейчас'}
          </Button>
        )}
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        Система сама прогоняет правила каждые 6 часов и раз в сутки делает дайджест. Найденные проблемы
        накапливаются здесь и <b>автоматически закрываются</b>, когда исчезают. Это история, а не разовый снимок
        (разовый — на странице «Проблемы»).
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {digest && (
        <Card sx={{ mb: 2, bgcolor: '#f8fafc' }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>{digest.title}</Typography>
            {digest.data && (
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {Object.entries(digest.data.issue_counts || {}).map(([sev, n]) => (
                  <Chip key={sev} size="small" color={SEV[sev] || 'default'} label={`${SEV_T[sev] || sev}: ${n}`} />
                ))}
                <Chip size="small" variant="outlined" label={`всего: ${digest.data.total_issues}`} />
              </Stack>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              сформирован: {new Date(digest.created_at).toLocaleString('ru-RU')}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(e, v) => setTab(v)}>
          <Tab label="Открытые" />
          <Tab label="Закрытые (история)" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : rows.length === 0 ? (
        <Alert severity={tab === 0 ? 'success' : 'info'}>{tab === 0 ? 'Открытых проблем нет — всё чисто.' : 'История пуста.'}</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Важность', 'Что', 'Детали', 'Объект', 'Обновлено'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 'bold' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((n) => (
                <TableRow key={n.id} hover>
                  <TableCell><Chip size="small" color={SEV[n.severity] || 'default'} label={SEV_T[n.severity] || n.severity} /></TableCell>
                  <TableCell>{n.title}</TableCell>
                  <TableCell sx={{ color: 'text.secondary', maxWidth: 320 }}>{n.body || '—'}</TableCell>
                  <TableCell>{n.entity_type ? `${n.entity_type}${n.entity_id ? ' #' + n.entity_id : ''}` : '—'}</TableCell>
                  <TableCell>{n.last_seen_at ? new Date(n.last_seen_at).toLocaleString('ru-RU') : (n.created_at ? new Date(n.created_at).toLocaleString('ru-RU') : '—')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default SystemChecks;
